import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronsUpDown } from "lucide-react";
import config from "../../config/config";
import { cn } from "../../lib/utils";
import InputShortText from "../InputShortText/InputShortText";
import { useCountryDetection } from "../../hooks/useCountryDetection";
import { useStoreCountry } from "../../hooks/useStoreCountry";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../Command/Command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover/Popover";

type PhoneNumberInputProps = {
  label?: string;
  defaultValue?: string;
  defaultCountry?: string;
  onChange?: (value: string) => void;
  validationFn?: (input: string) => boolean;
  isDisabled?: boolean;
  onValidationChange?: (isValid: boolean) => void;
};

type CountryDialOption = {
  code: string;
  nameKey: string;
  label: string;
  prefix: string;
  flag: string;
  searchValue: string;
};

const DEFAULT_COUNTRY_CODE = "PL";

const toCountryCode = (input?: string): string | undefined => {
  if (!input) return undefined;
  const upperInput = input.toUpperCase();
  const matchByCode = config.COUNTRY_CODES.find(
    (country) => country.countryCode === upperInput,
  );
  if (matchByCode) {
    return matchByCode.countryCode;
  }
  const lowerInput = input.toLowerCase();
  return config.COUNTRY_CODES.find(
    (country) => country.countryName === lowerInput,
  )?.countryCode;
};

const parsePhoneNumber = (
  phoneNumber: string,
  fallbackCountryCode: string,
): { countryCode: string; number: string } => {
    for (const country of config.COUNTRY_CODES) {
      if (phoneNumber.startsWith(country.prefix)) {
        return {
        countryCode: country.countryCode,
        number: phoneNumber.replace(country.prefix, ""),
        };
      }
    }

    return {
    countryCode: fallbackCountryCode,
      number: phoneNumber,
    };
  };  

const buildCountryOptions = (
  translate: (key: string) => string,
): CountryDialOption[] => {
  const sanitizeName = (value: string) =>
    value.toLowerCase().replace(/[_-]/g, " ");

  return [...config.COUNTRY_CODES]
    .map((country) => {
      const translatedName = translate(country.countryName) || country.countryName;
      const digitsOnly = country.prefix.replace(/\D/g, "");
      const searchTokens = [
        sanitizeName(translatedName),
        country.countryName.toLowerCase(),
        country.countryCode.toLowerCase(),
        country.prefix.toLowerCase(),
        digitsOnly,
        `+${digitsOnly}`,
      ]
        .filter(Boolean)
        .join(" ");

      return {
        code: country.countryCode,
        nameKey: country.countryName,
        label: translatedName,
        prefix: country.prefix,
        flag: country.flag,
        searchValue: searchTokens,
      };
    })
    .sort((a, b) => a.label.localeCompare(b.label));
};

const PhoneNumberInput: React.FC<PhoneNumberInputProps> = ({
  label,
  defaultValue,
  defaultCountry,
  onChange,
  validationFn,
  isDisabled,
  onValidationChange,
}) => {
  const { t } = useTranslation();
  const storeCountry = useStoreCountry();

  const countryOptions = useMemo(
    () => buildCountryOptions((key) => t(key)),
    [t],
  );

  const effectiveDefaultCountry = defaultCountry || storeCountry;
  const detectedCountry = useCountryDetection(effectiveDefaultCountry, DEFAULT_COUNTRY_CODE);
  const normalizedDefaultCountry = useMemo(() => {
    return toCountryCode(defaultCountry) ?? detectedCountry;
  }, [defaultCountry, detectedCountry]);
  const parsedPhone = useMemo(
    () => parsePhoneNumber(defaultValue || "", normalizedDefaultCountry),
    [defaultValue, normalizedDefaultCountry],
  );

  const [, setInternalValidity] = useState<boolean>(true);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    parsedPhone.countryCode,
  );
  const [phoneNumber, setPhoneNumber] = useState<string>(parsedPhone.number);
  const [open, setOpen] = useState(false);

  const selectedOption = countryOptions.find(
    (option) => option.code === selectedCountryCode,
  );

  const formatPhoneValue = (prefix: string, localNumber: string): string =>
    localNumber.trim() ? `${prefix}${localNumber}` : "";

  const handleCountrySelect = (countryCode: string) => {
    if (isDisabled) return;
    const option = countryOptions.find((item) => item.code === countryCode);
    if (!option) return;

    setSelectedCountryCode(option.code);
    setOpen(false);
  
    if (onChange) {
      onChange(formatPhoneValue(option.prefix, phoneNumber));
    }
  };

  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.currentTarget.value;
    const isValid = validationFn ? validationFn(value) : true;
    setInternalValidity(isValid);
    
    setPhoneNumber(value);
    
    if (onChange) {
      const prefix = selectedOption?.prefix ?? "";
      onChange(formatPhoneValue(prefix, value));
    }
  };

  useEffect(() => {
    const normalized =
      toCountryCode(defaultCountry) ?? detectedCountry;
    const { countryCode, number } = parsePhoneNumber(
      defaultValue || "",
      normalized,
    );
    setSelectedCountryCode(countryCode);
    setPhoneNumber(number);
  }, [defaultValue, defaultCountry, detectedCountry]);
  
  useEffect(() => {
    if (validationFn) {
      const valid = validationFn(phoneNumber);
      setInternalValidity(valid);
      if (onValidationChange) {
        onValidationChange(valid);
      }
    }
  }, [phoneNumber, selectedCountryCode, validationFn, onValidationChange]);

  return (
    <div className="flex flex-col w-full min-w-0">
      {label && <p className="text-sm font-extrabold">{t(label)}:</p>}
      <div className="flex items-center w-full gap-3 min-w-0">
        <div className="w-24 shrink-0">
          <Popover
            open={open}
            onOpenChange={(nextOpen) => {
              if (isDisabled) return;
              setOpen(nextOpen);
            }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                role="combobox"
                aria-expanded={open}
                aria-controls="country-select-popover"
                className={cn(
                  "flex w-full items-center justify-between rounded border border-stone-200 px-1 pt-3 pb-3 text-left focus:outline-none focus:ring-0",
                  isDisabled ? "cursor-not-allowed bg-stone-100 text-gray-500" : "cursor-pointer",
                )}
                disabled={isDisabled}
                data-dd-action-name="Country Dial Code Select"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <span className="text-lg">{selectedOption?.flag || "🌐"}</span>
                  {!selectedOption && (
                    <span className="truncate text-sm text-muted-foreground">
                      {t("global.null") || "Select country"}
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-1 shrink-0 min-w-0">
                  <span 
                    className="whitespace-nowrap px-1"
                    style={{
                      fontSize: selectedOption?.prefix 
                        ? (() => {
                            const len = selectedOption.prefix.length;
                            // Scale from 1rem (16px) for short prefixes to 0.8125rem (13px) for long ones
                            // Short prefixes (3-4 chars): ~1rem, Long prefixes (6+ chars): ~0.8125rem
                            const baseSize = 1; // 16px
                            const minSize = 0.8125; // 13px
                            const scale = Math.max(0, Math.min(1, (len - 3) / 3)); // Scale from 3 to 6 chars
                            return `${baseSize - (scale * (baseSize - minSize))}rem`;
                          })()
                        : '1rem'
                    }}
                  >
                    {selectedOption?.prefix || ""}
                  </span>
                  <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              id="country-select-popover"
              className="w-[320px] p-0"
              align="start"
            >
              <Command
                filter={(value, search) => {
                  if (!search) return 1;
                  const tokens = search
                    .toLowerCase()
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);
                  if (tokens.length === 0) {
                    return 1;
                  }
                  const haystack = value.toLowerCase();
                  return tokens.every((token) => haystack.includes(token)) ? 1 : 0;
                }}
              >
                <CommandInput
                  placeholder={t("customers.form.country_search_placeholder") || "Search country or prefix..."}
                  className="h-9"
                  disabled={isDisabled}
                />
                <CommandList className="max-h-[150px]">
                  <CommandEmpty>
                    {t("global.empty_results") || "No countries found."}
                  </CommandEmpty>
                  <CommandGroup>
                    {countryOptions.map((option) => (
                      <CommandItem
                        key={option.code}
                        value={option.searchValue}
                        onSelect={() => handleCountrySelect(option.code)}
                        data-dd-action-name="Country Dial Code Select Option"
                        className="flex items-center justify-between"
                      >
                        <span className="flex min-w-0 items-center gap-2">
                          <span className="text-lg">{option.flag}</span>
                          <span className="truncate">
                            {option.label}
                          </span>
                        </span>
                        <span className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{option.code}</span>
                          <span>{option.prefix}</span>
                          <Check
                            className={cn(
                              "h-4 w-4 shrink-0",
                              option.code === selectedCountryCode
                                ? "opacity-100"
                                : "opacity-0",
                            )}
                          />
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex-1 min-w-0">
          <InputShortText
              validationFn={validationFn}
              onChange={handlePhoneNumberChange}
              errorMessage={"errors.invalid_phone"}
              defaultValue={phoneNumber}
              isDisabled={isDisabled}
            onValidationChange={setInternalValidity}
          />
        </div>
      </div>
    </div>
  );
};

export default PhoneNumberInput;
